
import { useState } from 'react';
import { Student } from '@/services/db';
import { useStudents } from '@/contexts';
import { getTeachers } from '@/services/db/teachers';
import { getAdmins } from '@/services/db/admins';
import { toast } from 'sonner';
import { Admin } from '@/types/admin.types';

export type SearchResultType = 'all' | 'students' | 'teachers' | 'admins';

export interface UniversalSearchResult {
  students: Student[];
  teachers: any[];
  admins: Admin[];
}

/**
 * Custom hook for universal search across students, teachers, and admins
 */
export const useUniversalSearch = () => {
  const { searchStudents } = useStudents();
  const [searchResults, setSearchResults] = useState<UniversalSearchResult>({
    students: [],
    teachers: [],
    admins: []
  });
  const [isSearching, setIsSearching] = useState(false);

  const performSearch = async (query: string): Promise<UniversalSearchResult> => {
    if (!query.trim()) {
      return { students: [], teachers: [], admins: [] };
    }

    setIsSearching(true);
    const results: UniversalSearchResult = {
      students: [],
      teachers: [],
      admins: []
    };

    try {
      // Search for students
      const studentsResults = await searchStudents(query);
      results.students = studentsResults;

      // Search for teachers
      const teachers = await getTeachers();
      results.teachers = teachers.filter(teacher => 
        (teacher.firstName && teacher.firstName.toLowerCase().includes(query.toLowerCase())) ||
        (teacher.lastName && teacher.lastName.toLowerCase().includes(query.toLowerCase())) ||
        (teacher.email && teacher.email.toLowerCase().includes(query.toLowerCase())) ||
        (teacher.phone && teacher.phone.includes(query)) ||
        (teacher.department && teacher.department.toLowerCase().includes(query.toLowerCase()))
      );

      // Search for admins
      const admins = await getAdmins();
      results.admins = admins.filter(admin => 
        (admin.firstName && admin.firstName.toLowerCase().includes(query.toLowerCase())) ||
        (admin.lastName && admin.lastName.toLowerCase().includes(query.toLowerCase())) ||
        (admin.email && admin.email.toLowerCase().includes(query.toLowerCase())) ||
        (admin.position && admin.position.toLowerCase().includes(query.toLowerCase())) ||
        (admin.role && admin.role.toLowerCase().includes(query.toLowerCase()))
      );

      setSearchResults(results);
      return results;
    } catch (error) {
      console.error('Error performing universal search:', error);
      toast.error('Search failed. Please try again.');
      return { students: [], teachers: [], admins: [] };
    } finally {
      setIsSearching(false);
    }
  };

  return {
    searchResults,
    isSearching,
    performSearch
  };
};
